import axiosClient from './axiosClient';
import { ProjectEnvironmentModel } from '../Models/projectEnvironmentModel';
import { TicketSeverityModel } from '../Models/ticketSeverityModel';
import { TicketStatusModel } from '../Models/ticketStatusModel';

const projectService = {
  // ---------- Project_TBL ----------
  getAll: () => {
    return axiosClient.get('/Project')
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("GET /Project returned 404. Falling back to GET /projects...");
          return axiosClient.get('/projects');
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map((p) => ({
          projectID: p.projectId || p.projectID,
          projectCode: p.projectCode,
          projectName: p.projectName,
          description: p.description,
          clientOrgID: p.clientOrgId || p.clientOrgID,
          status: p.status,
          defaultEnvironmentID: p.defaultEnvironmentId || p.defaultEnvironmentID,
        }));
        return {
          data: mappedList,
        };
      });
  },

  getById: (projectID) => {
    return axiosClient.get(`/Project/${projectID}`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn(`GET /Project/${projectID} returned 404. Falling back to GET /projects/${projectID}...`);
          return axiosClient.get(`/projects/${projectID}`);
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const p = res?.data?.data || res?.data;
        if (!p) return { data: null };
        const mapped = {
          projectID: p.projectId || p.projectID,
          projectCode: p.projectCode,
          projectName: p.projectName,
          description: p.description,
          clientOrgID: p.clientOrgId || p.clientOrgID,
          status: p.status,
          defaultEnvironmentID: p.defaultEnvironmentId || p.defaultEnvironmentID,
        };
        return {
          data: mapped,
        };
      });
  },

  create: (payload) => {
    const mapped = {
      projectCode: payload.projectCode,
      projectName: payload.projectName,
      description: payload.description || '',
      clientOrgId: payload.clientOrgID ? Number(payload.clientOrgID) : null,
      status: payload.status || 'Active',
      defaultEnvironmentId: payload.defaultEnvironmentID ? Number(payload.defaultEnvironmentID) : null,
      nextTicketSequence: 1,
      createdBy: 1,
    };

    return axiosClient.post('/Project', mapped).catch((err) => {
      if (err?.response?.status === 404) {
        console.warn("POST /Project returned 404. Falling back to POST /projects...");
        return axiosClient.post('/projects', mapped);
      }
      return Promise.reject(err);
    });
  },

  update: (projectID, payload) => {
    const mapped = {
      projectId: Number(projectID),
      projectCode: payload.projectCode,
      projectName: payload.projectName,
      description: payload.description || '',
      clientOrgId: payload.clientOrgID ? Number(payload.clientOrgID) : null,
      status: payload.status || 'Active',
      defaultEnvironmentId: payload.defaultEnvironmentID ? Number(payload.defaultEnvironmentID) : null,
      lastUpdatedBy: 1,
    };

    return axiosClient.put('/Project', mapped).catch((err) => {
      if (err?.response?.status === 404) {
        console.warn("PUT /Project returned 404. Falling back to PUT /projects...");
        return axiosClient.put('/projects', mapped);
      }
      return Promise.reject(err);
    });
  },

  archive: (projectID) => {
    return projectService.getById(projectID).then((res) => {
      const p = res?.data;
      if (!p) return Promise.reject({ response: { data: { message: 'Project not found.' } } });
      const mapped = {
        projectId: Number(p.projectID),
        projectCode: p.projectCode,
        projectName: p.projectName,
        description: p.description || '',
        clientOrgId: p.clientOrgID ? Number(p.clientOrgID) : null,
        status: 'Archived',
        defaultEnvironmentId: p.defaultEnvironmentID ? Number(p.defaultEnvironmentID) : null,
        lastUpdatedBy: 1,
      };

      return axiosClient.put('/Project', mapped).catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("PUT /Project returned 404. Falling back to PUT /projects...");
          return axiosClient.put('/projects', mapped);
        }
        return Promise.reject(err);
      });
    });
  },

  remove: (projectID) => {
    return axiosClient.delete(`/Project/${projectID}`).catch((err) => {
      if (err?.response?.status === 404) {
        console.warn(`DELETE /Project/${projectID} returned 404. Falling back to DELETE /projects/${projectID}...`);
        return axiosClient.delete(`/projects/${projectID}`);
      }
      return Promise.reject(err);
    });
  },

  // ---------- Project_Phase_TBL ----------
  getAllPhases: () => {
    return axiosClient.get('/ProjectPhase')
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn("GET /ProjectPhase returned 404. Falling back to GET /ProjectPhases...");
          return axiosClient.get('/ProjectPhases');
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map((ph) => ({
          phaseID: ph.phaseId || ph.phaseID,
          projectID: ph.projectId || ph.projectID,
          phaseName: ph.phaseName,
          startDate: ph.startDate,
          endDate: ph.endDate,
          status: ph.status,
        }));
        return {
          data: mappedList,
        };
      });
  },

  getPhases: (projectID) => {
    return axiosClient.get(`/ProjectPhase/project/${projectID}`)
      .catch((err) => {
        console.warn(`GET /ProjectPhase/project/${projectID} failed. Falling back to GET /ProjectPhase...`);
        return axiosClient.get('/ProjectPhase')
          .catch((err2) => {
            if (err2?.response?.status === 404) {
              return axiosClient.get('/ProjectPhases');
            }
            return Promise.reject(err2);
          })
          .then((res) => {
            const rawList = res?.data?.data || res?.data || [];
            const filtered = rawList.filter(ph => Number(ph.projectId || ph.projectID) === Number(projectID));
            return { data: filtered };
          });
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map((ph) => ({
          phaseID: ph.phaseId || ph.phaseID,
          projectID: ph.projectId || ph.projectID,
          phaseName: ph.phaseName,
          startDate: ph.startDate,
          endDate: ph.endDate,
          status: ph.status,
        }));
        return {
          data: mappedList,
        };
      })
      .catch((err) => {
        console.error(`getPhases failed for project ${projectID}:`, err);
        return { data: [] };
      });
  },

  createPhaseDirect: (payload) => {
    const mapped = {
      projectId: Number(payload.projectID || payload.projectId),
      phaseName: payload.phaseName,
      startDate: payload.startDate,
      endDate: payload.endDate,
      status: payload.status || 'Active',
    };

    return axiosClient.post('/ProjectPhase', mapped).catch((err) => {
      if (err?.response?.status === 404) {
        console.warn("POST /ProjectPhase returned 404. Falling back to POST /ProjectPhases...");
        return axiosClient.post('/ProjectPhases', mapped);
      }
      return Promise.reject(err);
    });
  },

  updatePhaseDirect: (phaseID, payload) => {
    const mapped = {
      phaseId: Number(phaseID || payload.phaseID || payload.phaseId),
      projectId: Number(payload.projectID || payload.projectId),
      phaseName: payload.phaseName,
      startDate: payload.startDate,
      endDate: payload.endDate,
      status: payload.status,
    };

    return axiosClient.put('/ProjectPhase', mapped).catch((err) => {
      if (err?.response?.status === 404) {
        console.warn("PUT /ProjectPhase returned 404. Falling back to PUT /ProjectPhases...");
        return axiosClient.put('/ProjectPhases', mapped);
      }
      return Promise.reject(err);
    });
  },

  removePhaseDirect: (phaseID) => {
    return axiosClient.delete(`/ProjectPhase/${phaseID}`).catch((err) => {
      if (err?.response?.status === 404) {
        console.warn(`DELETE /ProjectPhase/${phaseID} returned 404. Falling back to DELETE /ProjectPhases/${phaseID}...`);
        return axiosClient.delete(`/ProjectPhases/${phaseID}`);
      }
      return Promise.reject(err);
    });
  },

  // Compatibility methods for detail views
  createPhase: (projectID, payload) => {
    return projectService.createPhaseDirect({ ...payload, projectID });
  },
  updatePhase: (projectID, phaseID, payload) => {
    return projectService.updatePhaseDirect(phaseID, { ...payload, projectID });
  },
  removePhase: (projectID, phaseID) => {
    return projectService.removePhaseDirect(phaseID);
  },

  // Mock placeholders for sub-resources
  getTeams: (projectID) => {
    return axiosClient.get(`/projects/${projectID}/teams`)
      .catch((err) => {
        if (err?.response?.status === 404) {
          console.warn(`GET /projects/${projectID}/teams returned 404. Falling back to GET /ProjectTeam...`);
          return axiosClient.get('/ProjectTeam')
            .catch((err2) => {
              if (err2?.response?.status === 404) {
                return axiosClient.get('/project-teams');
              }
              return Promise.reject(err2);
            })
            .then((res) => {
              const rawList = res?.data?.data || res?.data || [];
              const filtered = rawList.filter(t => Number(t.projectId || t.projectID) === Number(projectID));
              return { data: filtered };
            });
        }
        return Promise.reject(err);
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map((t) => ({
          teamID: t.teamId || t.teamID,
          projectID: t.projectId || t.projectID,
          teamName: t.teamName,
        }));
        return {
          data: mappedList,
        };
      });
  },
  getEnvironments: (projectID) => {
    return axiosClient.get(`/projects/${projectID}/environments`)
      .catch((err) => {
        console.warn(`GET /projects/${projectID}/environments returned 404. Falling back to GET /ProjectEnvironment...`);
        return axiosClient.get('/ProjectEnvironment')
          .catch((err2) => {
            if (err2?.response?.status === 404) {
              return axiosClient.get('/project-environments');
            }
            return Promise.reject(err2);
          })
          .then((res) => {
            const rawList = res?.data?.data || res?.data || [];
            const filtered = rawList.filter(env => Number(env.projectId || env.projectID) === Number(projectID));
            return { data: filtered };
          });
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map(ProjectEnvironmentModel.toFrontend);
        return {
          data: mappedList,
        };
      })
      .catch((err) => {
        console.error(`getEnvironments failed for project ${projectID}:`, err);
        return { data: [] };
      });
  },

  getStatuses: (projectID) => {
    return axiosClient.get(`/projects/${projectID}/statuses`)
      .catch((err) => {
        console.warn(`GET /projects/${projectID}/statuses returned 404. Falling back to GET /TicketStatus...`);
        return axiosClient.get('/TicketStatus')
          .catch((err2) => {
            if (err2?.response?.status === 404) {
              return axiosClient.get('/ticket-statuses');
            }
            return Promise.reject(err2);
          })
          .then((res) => {
            const rawList = res?.data?.data || res?.data || [];
            const filtered = rawList.filter(st => Number(st.projectId || st.projectID) === Number(projectID));
            return { data: filtered };
          });
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map(TicketStatusModel.toFrontend);
        return {
          data: mappedList,
        };
      })
      .catch((err) => {
        console.error(`getStatuses failed for project ${projectID}:`, err);
        return { data: [] };
      });
  },

  getSeverities: (projectID) => {
    return axiosClient.get(`/projects/${projectID}/severities`)
      .catch((err) => {
        console.warn(`GET /projects/${projectID}/severities returned 404. Falling back to GET /TicketSeverity...`);
        return axiosClient.get('/TicketSeverity')
          .catch((err2) => {
            if (err2?.response?.status === 404) {
              return axiosClient.get('/ticket-severities');
            }
            return Promise.reject(err2);
          })
          .then((res) => {
            const rawList = res?.data?.data || res?.data || [];
            const filtered = rawList.filter(sev => Number(sev.projectId || sev.projectID) === Number(projectID));
            return { data: filtered };
          });
      })
      .then((res) => {
        const rawList = res?.data?.data || res?.data || [];
        const mappedList = (Array.isArray(rawList) ? rawList : []).map(TicketSeverityModel.toFrontend);
        return {
          data: mappedList,
        };
      })
      .catch((err) => {
        console.error(`getSeverities failed for project ${projectID}:`, err);
        return { data: [] };
      });
  },
};

export default projectService;
